/**
 * Lightweight Bootstrap 5 toast helper that creates a one-off toast on the
 * fly, appends it to a body-level container, and auto-removes the DOM node
 * once the toast hides. Designed to be callable from non-Vue contexts
 * (axios interceptors, plain modules) where `this.$toast` / a local
 * component-bound Toast instance isn't available.
 *
 * Styling mirrors the pastel palette used by ClientDeploy.vue's local
 * toasts (top-right, soft background, fa-times close button), so the
 * global toast looks visually identical to component-bound ones.
 */
import { Toast } from 'bootstrap'

let toastContainer = null

function ensureContainer() {
	if (toastContainer && document.body.contains(toastContainer)) return toastContainer
	toastContainer = document.createElement('div')
	toastContainer.className = 'toast-container position-fixed top-0 end-0 p-3'
	toastContainer.style.zIndex = '11000'
	document.body.appendChild(toastContainer)
	return toastContainer
}

// Soft pastel palette — same as the component-bound toasts in
// ClientDeploy.vue (success: #f0faf3, danger: #fdecea, info: blue tint).
const PALETTE = {
	success: { bg: '#f0faf3', border: '#cdeede' },
	danger: { bg: '#fdecea', border: '#f5c2c0' },
	info: { bg: '#eaf4fd', border: '#bfdcfa' },
}

/**
 * Show a global Bootstrap toast.
 *
 * @param {string} message  Text content (plain text; HTML is escaped).
 * @param {'success'|'danger'|'info'} variant
 * @param {number} delay  Auto-hide delay in ms (default 4000).
 */
export function showGlobalToast(message, variant = 'info', delay = 4000) {
	const container = ensureContainer()
	const colors = PALETTE[variant] || PALETTE.info

	const el = document.createElement('div')
	el.className = 'toast'
	el.setAttribute('role', 'alert')
	el.setAttribute('aria-live', 'assertive')
	el.setAttribute('aria-atomic', 'true')
	el.style.background = colors.bg
	el.style.border = '1px solid ' + colors.border
	el.style.minWidth = '280px'
	el.style.borderRadius = '6px'
	el.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'

	const body = document.createElement('div')
	body.className = 'd-flex align-items-center'
	body.style.padding = '0.75rem 1rem'

	const text = document.createElement('span')
	text.style.flex = '1'
	text.style.fontSize = '0.9rem'
	text.textContent = message || ''

	const close = document.createElement('button')
	close.type = 'button'
	close.className = 'btn-close ms-2'
	close.setAttribute('data-bs-dismiss', 'toast')
	close.setAttribute('aria-label', 'Close')
	close.style.fontSize = '0.65rem'

	body.appendChild(text)
	body.appendChild(close)
	el.appendChild(body)
	container.appendChild(el)

	const toast = new Toast(el, { delay })
	el.addEventListener('hidden.bs.toast', () => el.remove())
	toast.show()
}
